import React, { useEffect, useRef, useState } from 'react';

interface TabProps {
  teamColor?: string;
  menuItems: { label: React.ReactNode; onClick: () => void }[];
  activeIndex: number;
}

const Tab: React.FC<TabProps> = ({ teamColor, menuItems, activeIndex }) => {
  const menuRef = useRef<HTMLUListElement>(null);
  const tabsRef = useRef<(HTMLButtonElement | null)[]>([]);
  const [indicatorStyle, setIndicatorStyle] = useState({
    width: 0,
    transform: 'translateX(0)'
  });

  useEffect(() => {
    if (menuRef.current && activeIndex >= 0 && activeIndex < menuRef.current.children.length) {
      const activeItem = menuRef.current.children[activeIndex] as HTMLElement;
      activeItem.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center'
      });
    }
  }, [activeIndex]);

  useEffect(() => {
    const updateIndicator = () => {
      const currentTab = tabsRef.current[activeIndex];
      if (!currentTab || !menuRef.current) return;

      const tabRect = currentTab.getBoundingClientRect();
      const containerRect = menuRef.current.getBoundingClientRect();

      const width = tabRect.width;
      const left = tabRect.left - containerRect.left + menuRef.current.scrollLeft;

      setIndicatorStyle({
        width,
        transform: `translateX(${left}px)`
      });
    };

    updateIndicator();

    window.addEventListener('resize', updateIndicator);
    const scrollContainer = menuRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', updateIndicator);
    }

    return () => {
      window.removeEventListener('resize', updateIndicator);
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', updateIndicator);
      }
    };
  }, [activeIndex, menuItems]);

  return (
    <>
      <style>
        {`
          .slider-container {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }

          .slider-container::-webkit-scrollbar {
            display: none;
          }
        `}
      </style>
      {menuItems.length > 0 && (
        <div className="w-full bg-white">
          <div className="relative">
            <ul
              ref={menuRef}
              className="slider-container w-full flex justify-center lg:justify-start space-x-2 overflow-y-hidden overflow-x-auto scroll-smooth border-b border-green-700"
              style={{
                scrollSnapType: 'x mandatory',
                WebkitOverflowScrolling: 'touch',
                borderBottom: `1px solid ${teamColor || ''}`,
                color: teamColor || ''
              }}>
              {menuItems.map((item, index) => (
                <li
                  key={index}
                  className={`relative flex-grow lg:flex-none flex justify-center whitespace-nowrap transition-all duration-200 ease-in-out
                    ${index === activeIndex ? 'active' : ''}`}>
                  <button
                    ref={(el) => (tabsRef.current[index] = el)}
                    type="button"
                    className={`flex items-center px-4 py-3 text-sm font-semibold transition-all duration-200 text-gray-400
                      ${index === activeIndex ? 'text-green-700' : 'text-gray-400'}`}
                    style={{
                      color: index === activeIndex ? teamColor : ''
                    }}
                    onClick={item.onClick}>
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
            {/* インジケーターバー */}
            <div
              className="absolute bottom-0 h-1 rounded-full transition-all duration-300 ease-out bg-custom-gradient2"
              style={{
                background: teamColor || '',
                width: `${indicatorStyle.width}px`,
                transform: indicatorStyle.transform,
                zIndex: 1
              }}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default Tab;
